/** @LearningJourney Authentication 6 - Authress implementation of AuthClient interface */
import { LoginClient } from "@authress/login";
import { type ReactNode, useState } from "react";
import { AuthProvider } from "../session/AuthProvider";
import { userSchema } from "../User";
import { RedirectToAuthressLogin } from "./RedirectToAuthressLogin";

export function AuthressAuthProvider({
  children,
  authressApiUrl,
  authressApplicationId,
  loadingFallback,
  audiences,
  connectionId,
}: {
  children: ReactNode;
  authressApiUrl: string;
  authressApplicationId: string;
  audiences?: string[];
  loadingFallback: ReactNode;
  connectionId?: string;
}) {
  const [authressLoginClient] = useState(() => {
    return new LoginClient({
      authressApiUrl,
      applicationId: authressApplicationId,
    });
  });

  return (
    <AuthProvider
      loginFallback={
        <RedirectToAuthressLogin
          authressClient={authressLoginClient}
          loadingFallback={loadingFallback}
        />
      }
      // Adapt Authress LoginClient to generic AuthClient interface
      authClient={{
        isLoggedIn:
          authressLoginClient.userSessionExists.bind(authressLoginClient),
        getToken: authressLoginClient.ensureToken.bind(authressLoginClient),
        getUser: async () => {
          await authressLoginClient.userSessionExists();
          const user = authressLoginClient.getUserIdentity();
          return userSchema.parse({
            id: user?.sub,
            email: user?.email,
            firstName: user?.given_name,
            lastName: user?.family_name,
          });
        },
        redirectToAuth: async ({ redirectBackTo }) => {
          await authressLoginClient.authenticate({
            audiences,
            redirectUrl: redirectBackTo.toString(),
            connectionId,
          });
        },
        logout: async () => {
          await authressLoginClient.authenticate({ audiences, connectionId });
        },
        linkIdentity: async ({
          redirectUrl,
          connectionId: targetConnectionId,
        }) => {
          await authressLoginClient.linkIdentity({
            redirectUrl,
            connectionId: targetConnectionId,
          });
        },
        getUserProfile:
          authressLoginClient.getUserProfile.bind(authressLoginClient),
        unlinkIdentity: async (identityId: string) => {
          await authressLoginClient.unlinkIdentity(identityId);
        },
        acceptInvite: async ({ inviteId }) => {
          await authressLoginClient.authenticate({
            inviteId,
            redirectUrl: window.location.href,
            audiences,
          });
        },
      }}
    >
      {children}
    </AuthProvider>
  );
}

// audiences specifies which APIs the token should be valid for
export function createAuthressAuthProvider({
  authressApiUrl,
  authressApplicationId,
  loadingFallback,
  audiences,
  connectionId,
}: {
  authressApiUrl: string;
  authressApplicationId: string;
  loadingFallback: ReactNode;
  audiences?: string[];
  connectionId?: string;
}) {
  return function AuthressAuthProviderWrapper({
    children,
  }: {
    children: ReactNode;
  }) {
    return (
      <AuthressAuthProvider
        authressApiUrl={authressApiUrl}
        authressApplicationId={authressApplicationId}
        loadingFallback={loadingFallback}
        audiences={audiences}
        connectionId={connectionId}
      >
        {children}
      </AuthressAuthProvider>
    );
  };
}
